import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import UploadImage from "../../components/UploadImage";
import "../../styles/Edit.css";
import backendUrl from "../../BackendUrl";

const EditPackage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const adminToken = localStorage.getItem("adminToken");

  const [packageData, setPackageData] = useState(null);
  const [trimOptions, setTrimOptions] = useState([]);
  const [models, setModels] = useState([]);
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch package data on mount.
  useEffect(() => {
    const fetchPackageData = async () => {
      try {
        const resPackage = await axios.get(
          `${backendUrl}/api/vehicles/get-package/${id}`
        );
        // If accessories is not present, initialize as empty array
        setPackageData({
          ...resPackage.data,
          accessories: Array.isArray(resPackage.data.accessories)
            ? resPackage.data.accessories
            : [],
        });
      } catch (err) {
        setError("Failed to fetch package data.");
      } finally {
        setLoading(false);
      }
    };
    fetchPackageData();
  }, [id]);

  // Once packageData is available, fetch models (by vehicleClassId) and then fetch trims.
  useEffect(() => {
    if (packageData?.vehicleClassId) {
      const fetchModelsAndTrims = async () => {
        try {
          const resModels = await axios.get(
            `${backendUrl}/api/vehicles/get-model`,
            { params: { vehicleClassId: packageData.vehicleClassId } }
          );
          setModels(resModels.data);

          const modelIds = resModels.data.map((m) => m._id);
          const resTrims = await axios.get(
            `${backendUrl}/api/vehicles/get-trim`,
            { params: { vehicleModelId: modelIds } }
          );
          setTrimOptions(resTrims.data);
        } catch (err) {
          console.error("Failed to fetch models or trims.", err);
        }
      };
      fetchModelsAndTrims();

      const fetchAccessoryOptions = async () => {
        try {
          const resAccessory = await axios.get(
            `${backendUrl}/api/vehicles/get-accessory`,
            { params: { vehicleClassId: packageData.vehicleClassId } }
          );
          setAccessoryOptions(resAccessory.data);
        } catch (err) {
          console.error("Failed to fetch accessory options.", err);
        }
      };
      fetchAccessoryOptions();
    }
  }, [packageData]);

  // Handlers for text inputs (name, description)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPackageData((prev) => ({ ...prev, [name]: value }));
  };

  // Handler for number inputs (price, mpg, mileage, etc.)
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setPackageData((prev) => ({ ...prev, [name]: Number(value) }));
  };

  // Handler to add an accessory name to the package
  const handleAddAccessory = (name) => {
    setPackageData((prev) => ({
      ...prev,
      accessories: [...(prev.accessories || []), name],
    }));
  };

  // Handler to remove an accessory name from the package
  const handleRemoveAccessory = (name) => {
    setPackageData((prev) => ({
      ...prev,
      accessories: (prev.accessories || []).filter((n) => n !== name),
    }));
  };

  // Update the package when the form is submitted.
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        `${backendUrl}/api/vehicles/update-package/${id}`,
        packageData,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setPackageData(response.data);
      alert("Package updated successfully!");
    } catch (error) {
      alert("Failed to update package. Please try again later.");
    }
  };

  // Delete the package.
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this package?")) return;
    try {
      await axios.delete(
        `${backendUrl}/api/vehicles/delete-package/${id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      alert("Package deleted successfully.");
      navigate("/edit-website");
    } catch (error) {
      alert("Failed to delete the package. Please try again later.");
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="error">{error}</p>;
  if (!packageData) return <p>No package data found.</p>;

  // Accessories available to add (not already in package)
  const availableAccessoryNames = accessoryOptions
    .map((acc) => acc.accessory)
    .filter(
      (name) => !(packageData.accessories || []).includes(name)
    );

  return (
    <div className="edit-class-container">
      {/* Left Section: Form */}
      <div className="edit-form">
        <h2>Edit Package</h2>
        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <label>
            Package Name:
            <input
              type="text"
              name="package"
              value={packageData.package || ""}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Description:
            <textarea
              name="description"
              value={packageData.description || ""}
              onChange={handleInputChange}
              rows={4}
            />
          </label>
          <label>
            Price:
            <input
              type="number"
              name="price"
              value={packageData.price ?? 0}
              onChange={handleNumberChange}
            />
          </label>

          {/* Performance Specs */}
          <h3>Performance Specs</h3>
          <label>
            MPG (City):
            <input
              type="number"
              name="mpgCity"
              value={packageData.mpgCity ?? 0}
              onChange={handleNumberChange}
            />
          </label>
          <label>
            MPG (Highway):
            <input
              type="number"
              name="mpgHwy"
              value={packageData.mpgHwy ?? 0}
              onChange={handleNumberChange}
            />
          </label>
          <label>
            MPG (Combined):
            <input
              type="number"
              name="mpgCmb"
              value={packageData.mpgCmb ?? 0}
              onChange={handleNumberChange}
            />
          </label>
          <label>
            Mileage:
            <input
              type="number"
              name="mileage"
              value={packageData.mileage ?? 0}
              onChange={handleNumberChange}
            />
          </label>
          <label>
            Weight:
            <input
              type="number"
              name="weight"
              value={packageData.weight ?? 0}
              onChange={handleNumberChange}
            />
          </label>
          <label>
            Seats:
            <input
              type="number"
              name="seats"
              value={packageData.seats ?? 0}
              onChange={handleNumberChange}
            />
          </label>
          <label>
            Engine:
            <input
              type="text"
              name="engine"
              value={packageData.engine || ""}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Transmission:
            <input
              type="text"
              name="transmission"
              value={packageData.transmission || ""}
              onChange={handleInputChange}
            />
          </label>

          {/* Group trims by model */}
          <div className="trims-section">
            <h3>Select Trims</h3>
            {models.map((model) => {
              const modelTrims = trimOptions.filter(
                (trim) => trim.vehicleModelId === model._id
              );
              if (!modelTrims.length) return null;
              return (
                <div key={model._id} className="model-trim-group">
                  <h4>{model.model}</h4>
                  {modelTrims.map((trim) => {
                    const isChecked = packageData.trimIds?.includes(trim._id);
                    return (
                      <div key={trim._id} className="trim-item">
                        <label>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              let updated = packageData.trimIds || [];
                              if (e.target.checked) {
                                updated = [...updated, trim._id];
                              } else {
                                updated = updated.filter((id) => id !== trim._id);
                              }
                              setPackageData((prev) => ({
                                ...prev,
                                trimIds: updated,
                              }));
                            }}
                          />
                          {trim.trim}
                        </label>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Accessory name list and add/remove UI */}
          <div className="trims-section">
            <h3>Package Accessories</h3>
            <ul>
              {(packageData.accessories || []).map((name) => (
                <li key={name} style={{ display: "flex", alignItems: "center" }}>
                  <span>{name}</span>
                  <button
                    type="button"
                    style={{
                      marginLeft: "8px",
                      color: "white",
                      background: "red",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      padding: "2px 8px",
                    }}
                    onClick={() => handleRemoveAccessory(name)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
            <div>
              <label>
                Add Accessory:
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddAccessory(e.target.value);
                    }
                  }}
                >
                  <option value="">Select accessory</option>
                  {availableAccessoryNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* READY TOGGLE */}
          <label className="toggle">
            <input
              type="checkbox"
              name="ready"
              checked={packageData?.ready || false}
              onChange={(e) =>
                setPackageData((prev) => ({
                  ...prev,
                  ready: e.target.checked,
                }))
              }
            />
            <span>Ready</span>
          </label>

          <button type="submit">Save Changes</button>
        </form>

        {/* UploadImage Component to update the package image */}
        <UploadImage
          uploadUrl="/api/vehicles/upload-package"
          id={id}
          onUploadSuccess={(data) => console.log("Uploaded:", data)}
        />

        {/* Delete button */}
        <button
          type="button"
          onClick={handleDelete}
          style={{ backgroundColor: "red", color: "white", marginTop: "1rem" }}
        >
          Delete Package
        </button>
      </div>

      {/* Right Section: Display Current Package Details */}
      <div className="display-section">
        {packageData.packageImg && (
          <div className="image-container">
            <img src={packageData.packageImg} alt="Package" />
            <div className="image-title">{packageData.package || "N/A"}</div>
          </div>
        )}
        <p className="description">
          {packageData.description || "No description available"}
        </p>
        {packageData.price !== undefined && (
          <p className="price">Price: ${packageData.price}</p>
        )}
      </div>
    </div>
  );
};

export default EditPackage;